import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import './CpMappedSalespersonPayment.css';
import { isValidEmail, isValidPhone, normalizePhone } from '../../Common/hooks/useManualPaymentConfig';
import { showUnknownPayment, unknownPaymentMessage } from '../../Common/utils/paymentResult';
import useUnmeRetryPayment, { isHandledUnmeRetry } from '../../Common/hooks/useUnmeRetryPayment';
import { classifyCpPayment, getPaymentCustomers, getPaymentSalespeople, submitCpMappedPayment } from '../api/mappedSalespersonPayment';

const emptyCard = () => ({ cardNo: '', month: '', year: '', installment: '00', email: '', phone: '' });
const digits = value => String(value).replace(/\D/g, '');
const newOrderId = () => 'cp_mapped_' + (window.crypto?.randomUUID?.() || Date.now() + '_' + Math.random().toString(36).slice(2));
const inputClass = 'cp-mapped-b__input';

export default function CpMappedSalespersonPayment({ config }) {
  const { runPayment, acknowledgeSuccessfulPayment, retryUi, retryBlocked } = useUnmeRetryPayment('cp');
  const [salespeople, setSalespeople] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [terminalId, setTerminalId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [customerError, setCustomerError] = useState('');
  const [reload, setReload] = useState(0);
  const [amount, setAmount] = useState('');
  const [card, setCard] = useState(emptyCard);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const submitLock = useRef(false);
  const orderId = useRef('');
  const mounted = useRef(true);
  const monthInput = useRef(null);
  const yearInput = useRef(null);
  const amountInput = useRef(null);

  useEffect(() => {
    mounted.current = true;
    const clear = () => setCard(emptyCard());
    window.addEventListener('pagehide', clear);
    window.addEventListener('pageshow', clear);
    return () => {
      mounted.current = false;
      window.removeEventListener('pagehide', clear);
      window.removeEventListener('pageshow', clear);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoadingSales(true);
    setSalesError('');
    getPaymentSalespeople(controller.signal).then(rows => {
      if (active) setSalespeople(rows);
    }).catch(() => {
      if (active) { setSalespeople([]); setSalesError('영업자 목록을 불러오지 못했습니다. 다시 조회해 주세요.'); }
    }).finally(() => { if (active) setLoadingSales(false); });
    return () => { active = false; controller.abort(); };
  }, [reload]);

  useEffect(() => {
    if (!terminalId) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoadingCustomers(true);
    setCustomerError('');
    getPaymentCustomers(terminalId, controller.signal).then(rows => {
      if (active) setCustomers(rows);
    }).catch(() => {
      if (active) { setCustomers([]); setCustomerError('배정 거래처를 불러오지 못했습니다. 다시 조회해 주세요.'); }
    }).finally(() => { if (active) setLoadingCustomers(false); });
    return () => { active = false; controller.abort(); };
  }, [terminalId, reload]);

  const salesperson = salespeople.find(row => String(row.id) === terminalId);
  const customer = customers.find(row => String(row.id) === customerId);
  const unresolved = outcome?.kind === 'unknown';
  const locked = busy || retryBlocked || unresolved || outcome?.kind === 'completed' || outcome?.kind === 'login';
  const amountNumber = Number(amount);
  const month = Number(card.month);
  const expiryValid = /^\d{2}$/.test(card.year) && month >= 1 && month <= 12
    && new Date(2000 + Number(card.year), month, 1).getTime() > Date.now();
  const canPay = config.loaded && !config.error && !config.loading
    && config.cpMappedSalespersonPaymentEnabled === true && config.paymentEnabled !== false
    && salesperson && customer && !loadingSales && !loadingCustomers && !locked
    && Number.isSafeInteger(amountNumber) && amountNumber > 0
    && /^\d{15,16}$/.test(card.cardNo) && expiryValid
    && (!config.buyerEmailRequired || isValidEmail(card.email.trim()))
    && (!config.buyerPhoneRequired || isValidPhone(normalizePhone(card.phone)));

  const changeTerminal = value => {
    if (locked) return;
    setTerminalId(value); setCustomerId(''); setCustomers([]);
    setCustomerError(''); setLoadingCustomers(Boolean(value));
    setCard(emptyCard()); setAmount(''); setOutcome(null); orderId.current = '';
  };
  const changeCustomer = value => {
    if (locked) return;
    setCustomerId(value); setCard(emptyCard()); setAmount(''); setOutcome(null); orderId.current = '';
  };
  const updateCard = (key, value) => setCard(current => ({ ...current, [key]: value }));

  const submit = async event => {
    event.preventDefault();
    if (!canPay || submitLock.current) return;
    submitLock.current = true; setBusy(true); setOutcome(null);
    if (!orderId.current) orderId.current = newOrderId();
    const summary = { salesperson: salesperson.name, customer: customer.name, amount: amountNumber };
    let body;
    try {
      const data = await runPayment({
        terminalId: Number(terminalId), customerMapId: Number(customerId), mchtTrdNo: orderId.current,
        trdAmt: amountNumber, instmtMon: amountNumber < 50000 ? '00' : card.installment,
        cardNo: card.cardNo, vldDtMon: card.month.padStart(2, '0'), vldDtYear: card.year,
        ...(config.buyerEmailRequired ? { email: card.email.trim() } : {}),
        ...(config.buyerPhoneRequired ? { phone: normalizePhone(card.phone) } : {}),
      }, submitCpMappedPayment, { customer: customer.name });
      body = { code: 1000, data };
    } catch (error) {
      if (isHandledUnmeRetry(error)) {
        if (mounted.current) {
          setCard(emptyCard());
          if (!error.blocked) orderId.current = '';
          submitLock.current = false;
          setBusy(false);
        }
        return;
      }
      // Never render or log raw PG errors (Axios errors can contain card request data).
      body = error?.response?.data || { code: error?.code, data: error?.data };
    }
    if (!mounted.current) return;
    const kind = classifyCpPayment(body);
    const code = Number(body?.code);
    const messages = {
      completed: '결제가 완료되었습니다.',
      failed: '결제가 승인되지 않았습니다. 카드정보를 확인해 주세요.',
      unknown: unknownPaymentMessage(),
      login: '로그인이 만료되었습니다. 결제내역을 확인한 후 다시 로그인해 주세요.',
      rejected: code === 4040 ? '영업자 또는 거래처 배정이 변경되었습니다. 다시 선택해 주세요.'
        : code === 4000 ? '입력값을 확인하고 다시 시도해 주세요.' : '현재 결제가 허용되지 않습니다. 관리자에게 문의해 주세요.',
    };
    setOutcome({ kind, message: messages[kind], ...summary,
      transactionId: body?.data?.paymentTransactionId,
      approvalNo: body?.data?.approvalNo || body?.data?.provider_result?.applNo || '',
    });
    setCard(emptyCard());
    if (kind === 'unknown') {
      if (body?.data?.paymentTransactionId) showUnknownPayment({ ...body.data, status: 'UNKNOWN' }, 'cp');
    } else {
      orderId.current = '';
    }
    if (kind === 'rejected' && code === 4040) {
      setCustomerId(''); setCustomers([]); setReload(value => value + 1);
    }
    submitLock.current = false; setBusy(false);
  };

  const retryLists = () => {
    if (locked) return;
    setCustomerId(''); setCustomers([]); setReload(value => value + 1);
  };
  const salesOptions = salespeople.map(row => ({ value: String(row.id), label: row.name + ' (' + row.terminal_id + ')', name: row.name }));
  const customerOptions = customers.map(row => ({ value: String(row.id), label: row.name + ' (' + row.external_customer_code + ')', name: row.name }));

  return (
    <form onSubmit={submit} autoComplete="off" className="cp-mapped-b">
      {retryUi}
      <div className="cp-mapped-b__scroll">
        <fieldset disabled={locked} className="cp-mapped-b__fieldset">
          <div className="cp-mapped-b__row">
            <div className="cp-mapped-b__field" style={{ flex: 1.2 }}>
              <label className="cp-mapped-b__label" htmlFor="cp-mapped-salesperson">영업자</label>
              <PaymentSelect id="cp-mapped-salesperson" options={salesOptions} value={terminalId} onChange={changeTerminal}
                disabled={locked || loadingSales} loading={loadingSales} placeholder="영업자 선택" />
            </div>
            <div className="cp-mapped-b__field" style={{ flex: 1.8 }}>
              <label className="cp-mapped-b__label" htmlFor="cp-mapped-customer">배정 거래처</label>
              <PaymentSelect id="cp-mapped-customer" options={customerOptions} value={customerId} onChange={changeCustomer}
                disabled={locked || !terminalId || loadingCustomers} loading={loadingCustomers}
                placeholder={terminalId ? '거래처 검색·선택' : '영업자 먼저 선택'} />
            </div>
          </div>
          {!loadingSales && !salesError && salespeople.length === 0 && <p role="status" className="cp-mapped-b__notice">선택 가능한 영업자가 없습니다.</p>}
          {terminalId && !loadingCustomers && !customerError && customers.length === 0 && <p role="status" className="cp-mapped-b__notice">선택 가능한 배정 거래처가 없습니다.</p>}
          {(salesError || customerError) && <div role="alert" className="cp-mapped-b__notice text-red-700">{salesError || customerError} <button type="button" className="underline" onClick={retryLists}>다시 조회</button></div>}
        </fieldset>
        <fieldset disabled={!customer || locked || config.paymentEnabled === false} className="cp-mapped-b__fieldset">
          <div className="cp-mapped-b__row">
            <Field label="카드번호" grow={2.5}>
              <input className={inputClass + ' cp-mapped-b__card-number'} inputMode="numeric" autoComplete="off" data-lpignore="true"
                value={card.cardNo.replace(/(.{4})/g, '$1 ').trim()} onChange={e => {
                  const value = digits(e.target.value).slice(0, 16); updateCard('cardNo', value);
                  if (value.length === 16) monthInput.current?.focus();
                }} placeholder="1234 5678 9012 3456" />
            </Field>
            <div className="cp-mapped-b__field" style={{ flex: 1 }}>
              <span className="cp-mapped-b__label">유효기간</span>
              <div className="cp-mapped-b__expiration">
                <input ref={monthInput} aria-label="유효기간 월" inputMode="numeric" autoComplete="off" value={card.month} onChange={e => {
                  const value = digits(e.target.value).slice(0, 2); updateCard('month', value);
                  if (value.length === 2 && Number(value) >= 1 && Number(value) <= 12) yearInput.current?.focus();
                }} placeholder="월" />
                <span aria-hidden="true">/</span>
                <input ref={yearInput} aria-label="유효기간 년" inputMode="numeric" autoComplete="off" value={card.year} onChange={e => {
                  const value = digits(e.target.value).slice(0, 2); updateCard('year', value);
                  if (value.length === 2) amountInput.current?.focus();
                }} placeholder="년" />
              </div>
            </div>
          </div>
          <div className="cp-mapped-b__row">
            <Field label="결제금액" grow={2.5}><input ref={amountInput} className={inputClass} inputMode="numeric" value={amount ? Number(amount).toLocaleString('ko-KR') : ''} onChange={e => {
              const next = digits(e.target.value); setAmount(next);
              if (Number(next) < 50000) updateCard('installment', '00');
            }} placeholder="결제금액" /></Field>
            <Field label="할부" grow={1}><select className={inputClass} value={card.installment} disabled={amountNumber < 50000} onChange={e => updateCard('installment', e.target.value)}>
              <option value="00">일시불</option>
              {Array.from({ length: 9 }, (_, i) => i + 2).map(value => <option key={value} value={String(value).padStart(2, '0')}>{value}개월</option>)}
            </select></Field>
          </div>
          {config.buyerEmailRequired && <div className="cp-mapped-b__row"><Field label="구매자 이메일"><input className={inputClass} type="email" autoComplete="off" value={card.email} onChange={e => updateCard('email', e.target.value)} /></Field></div>}
          {config.buyerPhoneRequired && <div className="cp-mapped-b__row"><Field label="구매자 연락처"><input className={inputClass} type="tel" autoComplete="off" value={card.phone} onChange={e => updateCard('phone', digits(e.target.value).slice(0, 15))} /></Field></div>}
        </fieldset>
        {outcome && <section role="status" aria-live="polite" className="rounded-xl border border-slate-300 bg-slate-50 p-4 space-y-3">
          <p className="font-semibold">{outcome.message}</p>
          <p>{outcome.salesperson} · {outcome.customer} · {outcome.amount.toLocaleString('ko-KR')}원</p>
          {outcome.approvalNo && <p>승인번호 {outcome.approvalNo}</p>}
          <a href="/cp/integrated-settlement" target="_blank" rel="noopener noreferrer" className="block font-semibold text-[var(--brand-primary)] underline">결제내역·전표 확인</a>
          {outcome.kind === 'completed' && <button type="button" onClick={() => { if (acknowledgeSuccessfulPayment()) { setOutcome(null); setAmount(''); } }} className="underline">새 결제</button>}
          {outcome.kind === 'login' && <a href="/cp/login" className="block underline">다시 로그인</a>}
        </section>}
      </div>
      <div className="cp-mapped-b__bottom">
        {config.paymentEnabled === false && <p role="alert" className="mb-3 text-sm text-red-700">{config.paymentBlockReason || '현재 결제가 허용되지 않습니다.'}</p>}
        {busy && <p role="status" className="mb-3 text-sm">결제 처리 중입니다. 화면을 닫지 마세요.</p>}
        <button type="submit" disabled={!canPay || busy} aria-busy={busy} className="cp-mapped-b__submit">{busy ? '결제 처리 중…' : '결제하기'}</button>
      </div>
    </form>
  );
}

function Field({ label, children, grow = 1 }) {
  return <label className="cp-mapped-b__field" style={{ flex: grow }}><span className="cp-mapped-b__label">{label}</span>{children}</label>;
}

function PaymentSelect({ id, options, value, onChange, disabled, loading, placeholder }) {
  return <Select inputId={id} instanceId={id} options={options} value={options.find(option => option.value === value) || null}
    onChange={option => onChange(option?.value || '')} isDisabled={disabled} isLoading={loading}
    isSearchable isClearable={false} placeholder={loading ? '조회 중…' : placeholder}
    noOptionsMessage={() => '검색 결과가 없습니다.'} loadingMessage={() => '조회 중…'}
    formatOptionLabel={(option, meta) => meta.context === 'value' ? option.name : option.label}
    menuPortalTarget={document.body} menuPosition="fixed" menuShouldScrollIntoView={false}
    styles={selectStyles} />;
}

const selectStyles = {
  container: base => ({ ...base, minWidth: 0 }),
  control: (base, state) => ({ ...base, minHeight: 35, height: 35, border: 0, borderBottom: '1px solid ' + (state.isFocused ? 'var(--brand-primary)' : '#f0f0f0'), borderRadius: 0, boxShadow: 'none', background: 'transparent', '&:hover': { borderColor: 'var(--brand-primary)' } }),
  valueContainer: base => ({ ...base, padding: '0 4px', flexWrap: 'nowrap' }),
  input: base => ({ ...base, margin: 0, padding: 0, fontSize: 16, color: '#191f28' }),
  placeholder: base => ({ ...base, margin: 0, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#9aa4b2' }),
  singleValue: base => ({ ...base, margin: 0, fontSize: 18, fontWeight: 600, color: '#191f28' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, padding: '0 2px', color: '#6b7280' }),
  clearIndicator: base => ({ ...base, padding: 0 }),
  menuPortal: base => ({ ...base, zIndex: 3500 }),
  menu: base => ({ ...base, border: '1px solid #e5e8eb', borderRadius: 10, boxShadow: '0 8px 20px rgba(15,23,42,.11)', overflow: 'hidden' }),
  option: (base, state) => ({ ...base, padding: '11px 10px', fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere', background: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-primary-soft)' : '#fff', color: state.isSelected ? '#fff' : '#191f28' }),
};
